import React, { useMemo } from 'react';

import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Link as ReactLink } from 'react-router-dom';

import FacebookIcon from '../../assets/icons/FacebookIcon';
import InstagramIcon from '../../assets/icons/InstagramIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import IKImage from '../../components/IKImage/IKImage';
import styled from '../../styled';
import { BIG_MARGIN, colorsPalette, LARGE_MARGIN, MIDDLE_MARGIN, SMALL_MARGIN } from '../../theme';
import { Navigation, NavigationItemType, NavigationSection } from '../../types';
import { checkIfInternalLink } from '../../utils/helpers';

interface FooterProps {
  footerPartners: FooterPartnersType[];
  navigation?: Navigation;
  tenantTitle?: string | null;
  linkedinLink?: string | null;
  twitterLink?: string | null;
  facebookLink?: string | null;
  instagramLink?: string | null;
}

interface FooterPartnersType {
  id: number | string;
  image_url: string;
  title: string;
}

const Footer: React.FC<FooterProps> = ({
  footerPartners,
  navigation,
  tenantTitle,
  linkedinLink,
  twitterLink,
  facebookLink,
  instagramLink,
}) => {
  const year = new Date().getFullYear();

  const navigationSections: NavigationSection[] = useMemo(() => {
    const sections = navigation?.items
      ? (navigation.items.filter(
          (item) => item.item_type === NavigationItemType.Section,
        ) as NavigationSection[])
      : [];

    return sections.map((section) => ({
      ...section,
      items: section?.items ? section.items.filter((item) => item?.type === 'Link') : [],
    }));
  }, [navigation]);

  return (
    <Wrapper>
      <FooterContainer component="footer">
        <StyledFooter>
          {footerPartners && (
            <Box>
              <FooterTitle component="h3">Accredited by</FooterTitle>
              <FooterPartners>
                {footerPartners.map((item) => (
                  <FooterPartner key={item.id}>
                    <IKImage
                      transformation={[{ height: 52 }]}
                      path={item.image_url}
                      draggable={false}
                      alt={item.title}
                    />
                  </FooterPartner>
                ))}
              </FooterPartners>
            </Box>
          )}
          <FooterNavigation>
            {navigationSections.map((block) => (
              <FooterLinksBlock key={block.title}>
                <FooterTitle component="h3">{block.title}</FooterTitle>
                {block.items &&
                  block.items.map((item) => {
                    const isInternalLink = item?.url && checkIfInternalLink(item.url);
                    return isInternalLink ? (
                      <FooterLink key={item?.url} component={ReactLink} to={item?.url}>
                        {item?.title}
                      </FooterLink>
                    ) : (
                      <FooterLink key={item?.url} href={item?.url}>
                        {item?.title}
                      </FooterLink>
                    );
                  })}
              </FooterLinksBlock>
            ))}
          </FooterNavigation>
        </StyledFooter>
        <BottomFooter>
          <SocialLinks>
            {linkedinLink && (
              <SocialIcon
                href={linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="linkedin_link"
              >
                <LinkedInIcon color={colorsPalette.white} />
              </SocialIcon>
            )}
            {instagramLink && (
              <SocialIcon
                href={instagramLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="instagram_link"
              >
                <InstagramIcon color={colorsPalette.white} />
              </SocialIcon>
            )}
            {facebookLink && (
              <SocialIcon
                href={facebookLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="facebook_link"
              >
                <FacebookIcon color={colorsPalette.white} />
              </SocialIcon>
            )}
            {twitterLink && (
              <SocialIcon
                href={twitterLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="twitter_link"
              >
                <TwitterIcon color={colorsPalette.white} />
              </SocialIcon>
            )}
          </SocialLinks>
          <License>
            © {tenantTitle} {year}
          </License>
        </BottomFooter>
      </FooterContainer>
    </Wrapper>
  );
};

export default Footer;

const Wrapper = styled(Box)`
  display: flex;
  justify-content: center;
  background: ${colorsPalette.blueDark};
  padding: 0 ${SMALL_MARGIN}px;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    padding: 0 90px;
  }
`;

const FooterContainer = styled(Box)`
  flex: 1;
  max-width: 1600px;
`;

const StyledFooter = styled(Box)`
  padding: ${MIDDLE_MARGIN}px 0;
  border-bottom: 1px solid ${alpha(colorsPalette.white, 0.33)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: ${LARGE_MARGIN}px 0;
  }
`;

const FooterTitle = styled(Typography)`
  color: ${colorsPalette.white};
  font-weight: 700;
  font-size: 18px;
  line-height: 1.4;
  margin-bottom: ${SMALL_MARGIN}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 22px;
    margin-bottom: 30px;
  }
`;

const FooterPartners = styled('div')`
  margin: auto;
  padding-bottom: ${MIDDLE_MARGIN}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-right: ${MIDDLE_MARGIN}px;
  }
`;

const FooterPartner = styled(Box)`
  display: inline-block;

  &:not(:last-child) {
    margin-right: 30px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      margin-right: ${MIDDLE_MARGIN}px;
    }
  }

  & > img {
    height: 36px;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      height: 42px;
    }

    ${({ theme }) => theme.breakpoints.up('md')} {
      height: 44px;
    }
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 600px;
  }
`;

const FooterNavigation = styled(Box)`
  display: flex;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-wrap: wrap;
  }
`;

const FooterLinksBlock = styled(Box)`
  &:not(:last-child) {
    padding-right: ${MIDDLE_MARGIN}px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      padding-right: ${LARGE_MARGIN}px;
    }
  }
`;

const FooterLink = styled(Link)`
  display: block;
  color: ${colorsPalette.white} !important;
  font-size: 16px;
  line-height: 1.4;
  margin-bottom: 16px !important;

  &:hover {
    text-decoration: none;
    color: ${colorsPalette.blue} !important;
  }
`;

const BottomFooter = styled(Box)`
  padding: ${MIDDLE_MARGIN}px 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${BIG_MARGIN}px 0;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;

const SocialLinks = styled(Box)`
  display: flex;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-bottom: 30px;
  }
`;

const SocialIcon = styled('a')`
  &:not(:first-child) {
    margin-left: ${SMALL_MARGIN}px;
  }

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

const License = styled(Box)`
  font-size: 18px;
  line-height: 1;
  color: ${colorsPalette.white};
`;
